import {
  mdiAlertCircleOutline,
  mdiCloudCheckOutline,
  mdiCloudClockOutline,
  mdiCloudOffOutline,
  mdiSyncAlert,
} from "@mdi/js"
import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
export type CalendarSyncBadgeKind =
  | "synced"
  | "pending"
  | "failed"
  | "not_syncable"
  | "review_needed"

export type CalendarSyncBadge = {
  kind: CalendarSyncBadgeKind
  label: string
  tooltip: string
  color: StatusDefinition["color"]
  icon: string
}

const calendarSyncableStatuses = new Set<ScheduleEventStatus>([
  "RESERVED",
  "ANNOUNCED",
  "CONFIRMED",
  "COMPLETED",
])

const isLocallySyncableEvent = (event: ScheduleEvent) =>
  Boolean(event.employee_id) && calendarSyncableStatuses.has(event.status)

export const resolveCalendarSyncBadge = (
  event: ScheduleEvent,
  calendarSyncEnabled: boolean,
): CalendarSyncBadge | null => {
  if (!calendarSyncEnabled) return null

  const sync = event.calendar_sync

  if (sync?.state === "PENDING") {
    return {
      kind: "pending",
      label: "Wartet auf Kalender",
      color: "blue",
      icon: mdiCloudClockOutline,
      tooltip: "Wird mit dem verbundenen Kalender abgeglichen.",
    }
  }

  if (sync?.state === "NEEDS_REVIEW") {
    return {
      kind: "review_needed",
      label: "Prüfen",
      color: "rose",
      icon: mdiAlertCircleOutline,
      tooltip:
        "Der Termin konnte nicht übernommen werden. Bitte den Termin prüfen und den Kalenderabgleich erneut starten.",
    }
  }

  if (sync?.state === "ERROR") {
    return {
      kind: "failed",
      label: "Fehler",
      color: "rose",
      icon: mdiSyncAlert,
      tooltip:
        "Bitte den Kalenderabgleich erneut starten. Bleibt der Fehler bestehen, die Kalenderverbindung prüfen.",
    }
  }

  if (!isLocallySyncableEvent(event)) {
    return {
      kind: "not_syncable",
      label: "Nicht im Kalender",
      color: "neutral",
      icon: mdiCloudOffOutline,
      tooltip: !event.employee_id
        ? "Bitte einen Mitarbeiter zuordnen, damit der Termin in dessen Kalender erscheint."
        : event.status === "CANCELED"
          ? "Abgesagte Termine werden nicht in den Kalender übertragen."
          : event.status === "NOSHOW"
            ? "Dieser Termin hat nicht stattgefunden und wird nicht in den Kalender übertragen."
            : "Dieser Termin wird in seinem aktuellen Zustand nicht in den Kalender übertragen.",
    }
  }

  if (sync?.links.length && sync.state === "OK") {
    return {
      kind: "synced",
      label: "Im Kalender",
      color: "emerald",
      icon: mdiCloudCheckOutline,
      tooltip: "",
    }
  }

  return {
    kind: "pending",
    label: "Wartet",
    color: "blue",
    icon: mdiCloudClockOutline,
    tooltip: "Wird beim nächsten Kalenderabgleich übertragen.",
  }
}
